import { useTranslations } from 'next-intl';
import { Text } from '@/components/ui/text/Text';
import { TextAlign } from '@/components/ui/text/Text.types';
import { Status } from '@/components/ui/status';
import { StatusState, StatusType } from '@/components/ui/status/Status.types';
import { Colors } from '@/lib/colorUtils';
import { cn } from '@/lib/styleUtils';
import {
  formatReservationTimeInGarageTz,
  formatReservationDateInGarageTz,
  getDurationMinutes,
  formatDurationHoursMinutes,
  ceilToMinute,
} from '@/lib/date-time-utils';
import type { ReservationTimeDetailsProps } from './ReservationTimeDetails.types';

export const ReservationTimeDetails = ({
  startDate,
  endDate,
  garageTimezone,
  variant = 'reservation',
  timeSize = '24',
  className,
}: ReservationTimeDetailsProps) => {
  const t = useTranslations('Reservations');
  const isTicket = variant === 'ticket';
  const enterLabel = isTicket ? t('enter') : t('enterAfter');
  const exitLabel = isTicket ? t('exit') : t('exitBefore');
  const timeClass = 'tw:tracking-[0.3px]';
  const enterAfter = formatReservationTimeInGarageTz(startDate, garageTimezone);
  const enterAfterDate = formatReservationDateInGarageTz(startDate, garageTimezone);

  // PB-8094: a reservation's exit boundary is the last covered instant (enter + Nh − 1s, e.g.
  // 10:44:59 for a 2h stay), so rendering it raw read as 10:44 / 1:59 instead of the full 10:45 / 2:00.
  // Show the exclusive minute boundary. Tickets keep their exact checkout timestamp — this is a
  // reservation-window display convention, not a change to recorded ticket times.
  const displayExit = endDate != null && !isTicket ? ceilToMinute(endDate) : endDate;
  // Derive hasExit from displayExit (null iff endDate is) so the aliased-condition narrowing
  // applies to displayExit inside each `hasExit ?` branch below.
  const hasExit = displayExit != null;
  const exitBefore = hasExit ? formatReservationTimeInGarageTz(displayExit, garageTimezone) : undefined;
  const exitBeforeDate = hasExit ? formatReservationDateInGarageTz(displayExit, garageTimezone) : undefined;

  const totalMinutes = hasExit ? getDurationMinutes(startDate, displayExit) : 0;
  const durationText = hasExit
    ? t('durationHoursMinutes', {
        duration: formatDurationHoursMinutes(totalMinutes),
        // Fractional hours so ICU pluralizes singular only for an exact 1:00.
        hours: totalMinutes / 60,
      })
    : undefined;

  return (
    <div className={cn('tw:flex tw:flex-col tw:gap-micro', className)} data-cy="reservation-time-details">
      <div className="tw:flex tw:items-center">
        <Text
          size="12"
          weight="regular"
          lineHeight="1"
          color={Colors.colorGrayDarker}
          className="tw:whitespace-nowrap tw:mr-small"
        >
          {enterLabel}
        </Text>
        <Status status={StatusState.positive} type={StatusType.icon} className="tw:h-3 tw:w-3" />

        <div className="tw:flex-1 tw:flex tw:items-center">
          <div className="tw:flex-1 tw:h-px tw:bg-gray-light" />
          {durationText && (
            <div className="tw:border tw:border-solid tw:border-gray-light tw:rounded-full tw:px-small tw:py-nano">
              <Text
                size="12"
                weight="medium"
                color={Colors.colorGrayDark}
                className="tw:whitespace-nowrap"
                data-cy="reservation-time-duration"
              >
                {durationText}
              </Text>
            </div>
          )}
          <div className="tw:flex-1 tw:h-px tw:bg-gray-light" />
        </div>

        <Status
          status={hasExit ? StatusState.negative : StatusState.neutral}
          type={StatusType.icon}
          className="tw:h-3 tw:w-3"
        />
        <Text
          size="12"
          weight="regular"
          color={Colors.colorGrayDark}
          className="tw:whitespace-nowrap tw:ml-small"
          textAlign={TextAlign.right}
        >
          {exitLabel}
        </Text>
      </div>

      <div className="tw:flex tw:justify-between">
        <div className="tw:flex tw:flex-col tw:gap-nano">
          <Text size={timeSize} weight="bold" color={Colors.colorFont} className={timeClass} data-cy="reservation-time-enter">
            {enterAfter}
          </Text>
          <Text size="14" weight="regular" color={Colors.colorGrayDarker} data-cy="reservation-time-enter-date">
            {enterAfterDate}
          </Text>
        </div>
        <div className="tw:flex tw:flex-col tw:gap-nano tw:items-end">
          {hasExit ? (
            <>
              <Text
                size={timeSize}
                weight="bold"
                color={Colors.colorFont}
                textAlign={TextAlign.right}
                className={timeClass}
                data-cy="reservation-time-exit"
              >
                {exitBefore}
              </Text>
              <Text
                size="14"
                weight="regular"
                color={Colors.colorGrayDarker}
                textAlign={TextAlign.right}
                data-cy="reservation-time-exit-date"
              >
                {exitBeforeDate}
              </Text>
            </>
          ) : (
            <>
              <Text
                size={timeSize}
                weight="bold"
                color={Colors.colorAccent}
                textAlign={TextAlign.right}
                className={timeClass}
                data-cy="reservation-time-exit"
              >
                {t('openStatus')}
              </Text>
              <Text
                size="14"
                weight="regular"
                color={Colors.colorGrayDarker}
                textAlign={TextAlign.right}
                data-cy="reservation-time-exit-date"
              >
                {t('noExitSpecified')}
              </Text>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
